import React from "react"
import { Box, Typography } from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import {
	AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader,
	AppNote, AppValueChip, secondaryButtonSx,
} from "@components"
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { DS_REPRICING_ROUTES } from "@constants/routes"
import { dsColorNew } from "@palette"

// The conflicting rule opens in a NEW TAB on purpose: the user has to fix the other rule without
// losing the configuration they are writing here.
const openInNewTab = id => window.open(
	`${DS_REPRICING_ROUTES.CONFIGURATION_PATH}?rule=${encodeURIComponent(id)}`,
	'_blank',
	'noopener',
)

// `onConfirm` is only set for a non-blocking overlap: priority already breaks the tie, so the
// overlap is legal and just needs to be shown. It is null for a same-priority conflict, which has
// nothing to confirm past — the other rule has to change first.
export default function OverlapModal({ open, onClose, conflicts, onConfirm }) {
	const list = conflicts ?? []
	const blocking = list.filter(c => c.blocking)
	const resolved = list.filter(c => !c.blocking)

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="sm">
			<AppDialogHeader icon="triangle-exclamation" title="Rule overlap" onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				{blocking.length > 0 && (
					<AppNote severity="error" sx={{ mb: 2 }}>
						This rule coincides with {blocking.length === 1 ? 'another rule' : `${blocking.length} other rules`} on
						every axis — period (season, lead time or weekdays), office, ACRISS, duration, tariff type
						and insurance — at the <strong>same priority</strong>.
						Change the priority of one of them, or remove the setting affecting those parameters for
						that period in the other rule, then save this one.
					</AppNote>
				)}

				{resolved.length > 0 && (
					<AppNote sx={{ mb: 2 }}>
						This rule also overlaps {resolved.length === 1 ? 'another rule' : `${resolved.length} other rules`} on
						period, office, ACRISS, duration, tariff type and insurance, but at a different priority — the lower number wins
						where they meet. This is allowed; just confirm the hierarchy is what you intend.
					</AppNote>
				)}

				{list.map(conflict => (
					<Box
						key={conflict.id}
						sx={{
							mb: 1.5, px: 1.75, py: 1.5, borderRadius: '10px',
							border: `1px solid ${conflict.blocking ? dsColorNew.dangerBorder : dsColorNew.grayBorder}`,
							display: 'flex', alignItems: 'flex-start', gap: 1,
						}}
					>
						<Box sx={{ flex: 1 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle }}>
									{conflict.name}
								</Typography>
								<AppValueChip
									sx={conflict.blocking ? { backgroundColor: dsColorNew.dangerBg, color: dsColorNew.dangerRed, fontWeight: 600 } : {}}
								>
									{`Priority ${conflict.priority}`}
								</AppValueChip>
							</Box>

							{(conflict.periods ?? []).length > 0 && (
								<Box sx={{ mt: 1 }}>
									<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>Overlapping season</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
										{conflict.periods.map((p, i) => (
											<AppValueChip key={i}>{`${p.seasonFrom} → ${p.seasonTo}`}</AppValueChip>
										))}
									</Box>
								</Box>
							)}

							{(conflict.durations ?? []).length > 0 && (
								<Box sx={{ mt: 1 }}>
									<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>Overlapping duration</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
										{conflict.durations.map((d, i) => (
											<AppValueChip key={i}>{`${d.durationFrom} → ${d.durationTo} days`}</AppValueChip>
										))}
									</Box>
								</Box>
							)}
						</Box>

						<ButtonLoadingSeeker
							variant="outlined"
							startIcon={<IconSeeker icon="up-right-from-square"/>}
							onClick={() => openInNewTab(conflict.id)}
							sx={{ ...secondaryButtonSx, height: 34 }}
						>
							Open
						</ButtonLoadingSeeker>
					</Box>
				))}

				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
					The conflicting rule opens in a new tab, so nothing you have configured here is lost.
				</Typography>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}>
					{onConfirm ? 'Cancel' : 'Close'}
				</AppDialogCancel>
				{onConfirm && (
					<AppBulkEditAction startIcon={<IconSeeker icon="floppy-disk"/>} onClick={onConfirm}>
						Save anyway
					</AppBulkEditAction>
				)}
			</AppDialogFooter>
		</AppDialog>
	)
}